import { useEffect, useState } from 'react';

import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';

import type { SpaceOption } from './CreateKBCardPublishForm';

export const usePrefetchedSpaceOptions = ({
	cloudId,
	userId,
}: {
	cloudId: string;
	userId?: string;
}): {
	prefetchedSpaceOptions: SpaceOption[] | null;
	isPrefetchedSpaceOptionsLoading: boolean;
} => {
	const { listEntities } = useConfluenceEntities(cloudId);
	const [prefetchedSpaceOptions, setPrefetchedSpaceOptions] = useState<SpaceOption[] | null>(null);
	const [isPrefetchedSpaceOptionsLoading, setIsPrefetchedSpaceOptionsLoading] = useState(true);

	useEffect(() => {
		if (!userId) {
			setIsPrefetchedSpaceOptionsLoading(false);
			setPrefetchedSpaceOptions([]);
			setIsPrefetchedSpaceOptionsLoading(false);
			return;
		}

		let isCancelled = false;
		setIsPrefetchedSpaceOptionsLoading(true);

		const loadSpaces = async () => {
			setIsPrefetchedSpaceOptionsLoading(true);
			try {
				const entities = await listEntities('', ['space']);
				if (isCancelled) {
					return;
				}

				const prefetchedOptions = entities.reduce<SpaceOption[]>((spaces, entity) => {
					if (!entity.id || !entity.key) {
						return spaces;
					}

					spaces.push({
						label: entity.name,
						value: entity.id,
						key: entity.key,
					});
					return spaces;
				}, []);
				setPrefetchedSpaceOptions(prefetchedOptions);
			} catch (exception) {
				if (isCancelled) {
					return;
				}

				logException({
					exception,
					name: 'CreateKbCardLoadRecommendedSpacesError',
				});
				setPrefetchedSpaceOptions([]);
			} finally {
				if (!isCancelled) {
					setIsPrefetchedSpaceOptionsLoading(false);
				}
			}
		};

		void loadSpaces();
		return () => {
			isCancelled = true;
		};
	}, [listEntities, userId]);

	return { prefetchedSpaceOptions, isPrefetchedSpaceOptionsLoading };
};
